import React, { useContext } from "react"

export const EMBED_HOSTS = [
  "sz-base",
  "magazin",
] as const

export type EmbedHost = (typeof EMBED_HOSTS)[number]

export interface IQuizEmbedInfoContext {
  isEmbed: boolean;
  displayOutsideRanking?: boolean;
  displayOutsideSharing?: boolean;
  displayOutsidePersonalityTestPointsToResultInfo?: boolean;
  displayOutsideIntroduction?: boolean;
  displayOutsideTeasers?: boolean;
  host: EmbedHost;
  omitRoundNumbers?: boolean;
  colorPalettes: {
    cover: string[];
    question: string[];
    result: string[];
  };
  resultIcons: { // happy and sad faces
    positive: string[];
    positiveRespectful?: string[];
    negative: string[];
    question: string[];
    check: string[];
  };
}

const DEFAULT_WHEN_ON_STANDALONE_PAGE: Pick<IQuizEmbedInfoContext, "displayOutsideRanking" | "displayOutsideIntroduction" | "displayOutsidePersonalityTestPointsToResultInfo" | "displayOutsideSharing" | "displayOutsideTeasers" | "isEmbed"> = {
  isEmbed: false,
  displayOutsideIntroduction: true,
  displayOutsidePersonalityTestPointsToResultInfo: true,
  displayOutsideRanking: true,
  displayOutsideSharing: true,
  displayOutsideTeasers: true,
}

const DEFAULT_WHEN_EMBED: Pick<IQuizEmbedInfoContext, "displayOutsideRanking" | "displayOutsideIntroduction" | "displayOutsidePersonalityTestPointsToResultInfo" | "displayOutsideSharing" | "displayOutsideTeasers" | "isEmbed"> = {
  isEmbed: true,
  displayOutsideIntroduction: false,
  displayOutsidePersonalityTestPointsToResultInfo: true,
  displayOutsideRanking: true,
  displayOutsideSharing: false,
  displayOutsideTeasers: false,
}

const SZ_BASE_SETTINGS: Pick<IQuizEmbedInfoContext, "colorPalettes" | "host" | "omitRoundNumbers" | "resultIcons"> = {
  colorPalettes: {
    cover: ["0"],
    question: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", ],
    result: ["2"], // sz-green
  },
  host: "sz-base",
  resultIcons: {
    positive: ["happy--0", "happy--1", "happy--2", "happy--3", "happy--4", "happy--5"],
    positiveRespectful: ["happy--1", "happy--3", "happy--5"],
    negative: ["sad--0", "sad--1", "sad--2", "sad--3", "sad--4", "sad--5"],
    question: ["soso"],
    check: ["check"],
  },
}

const MAGAZIN_BASE_SETTINGS: Pick<IQuizEmbedInfoContext, "colorPalettes" | "host" | "omitRoundNumbers" | "resultIcons"> = {
  colorPalettes: {
    cover: ["magazin2"],
    question: ["magazin1", "magazin2", "magazin3", "magazin4" ],
    result: ["magazinResult"],
  },
  host: "magazin",
  omitRoundNumbers: true,
  resultIcons: {
    positive: ["magazinHappy1","magazinHappy2","magazinHappy3"],
    positiveRespectful: ["magazinHappy1","magazinHappy3"],
    negative: ["magazinFalsch1", "magazinFalsch2", "magazinFalsch3"],
    question: ["magazinQuestion"],
    check: ["magazinCheck"],
  },
}

export const EMBED_INFO_CONTEXT_SZ_ON_SEPARATE_PAGE: IQuizEmbedInfoContext = {
  ...SZ_BASE_SETTINGS,
  ...DEFAULT_WHEN_ON_STANDALONE_PAGE,
}

export const EMBED_INFO_CONTEXT_SZ: IQuizEmbedInfoContext = {
  ...SZ_BASE_SETTINGS,
  ...DEFAULT_WHEN_EMBED,
}

export const EMBED_INFO_CONTEXT_MAGAZIN: IQuizEmbedInfoContext = {
  ...MAGAZIN_BASE_SETTINGS,
  ...DEFAULT_WHEN_EMBED,
}

const QuizEmbedInfoContext = React.createContext<IQuizEmbedInfoContext>(EMBED_INFO_CONTEXT_SZ_ON_SEPARATE_PAGE)

export const useQuizEmbedInfo = () => useContext(QuizEmbedInfoContext)

export default QuizEmbedInfoContext
